import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, FileText, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const empty = { title: "", content: "", published: true };

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/pages", { params: { all: true } }).then(({ data }) => setPages(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ title: p.title, content: p.content, published: p.published }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) { toast.error("Συμπληρώστε τίτλο και περιεχόμενο"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/pages/${editing.id}`, form);
      else await api.post("/pages", form);
      toast.success(editing ? "Ενημερώθηκε" : "Δημιουργήθηκε");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Σφάλμα");
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Διαγραφή της σελίδας «${p.title}»;`)) return;
    await api.delete(`/pages/${p.id}`);
    toast.success("Διαγράφηκε");
    load();
  };

  const inputCls = "w-full px-3 py-2.5 rounded-sm border border-[#E5E9E4] bg-white text-sm focus:border-[#1E3A2F] focus:ring-1 focus:ring-[#1E3A2F] outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif-display text-3xl text-[#1E3A2F]">Σελίδες περιεχομένου</h1>
        <button onClick={openNew} data-testid="add-page-button" className="inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-[#111C18]">
          <Plus className="h-4 w-4" /> Νέα σελίδα
        </button>
      </div>
      <p className="text-[#536D61] mb-6 text-sm">π.χ. «Σχετικά με εμάς», «Επικοινωνία», «Όροι & Προϋποθέσεις». Εμφανίζονται στο υποσέλιδο.</p>

      {pages.length ? (
        <div className="space-y-3">
          {pages.map((p) => (
            <div key={p.id} className="bg-white border border-[#E5E9E4] rounded-sm p-4 flex items-center justify-between gap-4" data-testid={`page-row-${p.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[#1E3A2F]">{p.title}</p>
                  {!p.published && <span className="text-xs bg-[#f3e3e2] text-[#C25953] px-2 py-0.5 rounded-sm">Πρόχειρο</span>}
                </div>
                <p className="text-sm text-[#536D61] line-clamp-1 mt-0.5">{p.content}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Link to={`/page/${p.slug}`} target="_blank" className="p-2 text-[#536D61] hover:text-[#1E3A2F]"><ExternalLink className="h-4 w-4" /></Link>
                <button onClick={() => openEdit(p)} data-testid={`edit-page-${p.id}`} className="p-2 text-[#536D61] hover:text-[#1E3A2F]"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(p)} data-testid={`delete-page-${p.id}`} className="p-2 text-[#C25953] hover:text-[#a3433d]"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-[#E5E9E4] rounded-sm py-16 text-center text-[#536D61]">
          <FileText className="h-10 w-10 text-[#1E3A2F]/30 mx-auto mb-3" />
          Δεν υπάρχουν σελίδες ακόμη.
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto bg-black/50">
          <div className="bg-white rounded-sm w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E9E4]">
              <h2 className="font-serif-display text-2xl text-[#1E3A2F]">{editing ? "Επεξεργασία σελίδας" : "Νέα σελίδα"}</h2>
              <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-[#536D61]" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">Τίτλος *</label>
                <input data-testid="page-title" value={form.title} onChange={set("title")} className={inputCls} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1E3A2F] mb-1.5">Περιεχόμενο *</label>
                <textarea data-testid="page-content" value={form.content} onChange={set("content")} rows={10} className={inputCls} required />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#1E3A2F]">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="accent-[#1E3A2F]" /> Δημοσιευμένη
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E9E4]">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-sm border border-[#E5E9E4] text-sm">Ακύρωση</button>
                <button type="submit" disabled={saving} data-testid="save-page-button" className="px-5 py-2.5 rounded-sm bg-[#1E3A2F] text-white text-sm font-medium hover:bg-[#111C18] disabled:opacity-60">{saving ? "Αποθήκευση..." : "Αποθήκευση"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
